import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import RichTextEditor from './RichTextEditor';
import ModuleAnalytics from './ModuleAnalytics';
import Accordion from './Accordion';

export default function EducationModuleManager({ role, modules, onRefresh, loading }) {
  const [activeTab, setActiveTab] = useState('modules');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneReward, setCloneReward] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    tags: '',
    reward: { type: 'credit', creditAmount: 5 },
    screens: [{ title: '', content: '', type: 'text' }],
    showOnStorefront: false,
    nextModuleSlug: ''
  });

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      tags: '',
      reward: { type: 'credit', creditAmount: 5 },
      screens: [{ title: '', content: '', type: 'text' }],
      showOnStorefront: false,
      nextModuleSlug: ''
    });
    setEditingModule(null);
    setIsMinimized(false);
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (module) => {
    setEditingModule(module);
    setFormData({
      title: module.title || '',
      description: module.description || '',
      category: module.category || '',
      tags: module.tags ? module.tags.join(', ') : '',
      reward: module.reward || { type: 'credit', creditAmount: 5 },
      screens: module.screens || [{ title: '', content: '', type: 'text' }],
      showOnStorefront: module.showOnStorefront || false,
      nextModuleSlug: module.nextModuleSlug || ''
    });
    setIsMinimized(false);
    setShowEditModal(true);
  };

  const handleCreateModule = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      };
      
      const response = await fetch('/api/education-modules', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Module created successfully!');
        setShowCreateModal(false);
        resetForm();
        if (onRefresh) onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create module');
      }
    } catch (error) {
      console.error('Error creating module:', error);
      toast.error('Failed to create module');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateModule = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    // Prevent self-linking
    if (formData.nextModuleSlug === editingModule?.slug) {
      toast.error('Cannot link a module to itself!');
      setIsSubmitting(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      };
      
      const response = await fetch(`/api/education-modules/${editingModule._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Module updated successfully!');
        setShowEditModal(false);
        resetForm();
        if (onRefresh) onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update module');
      }
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error('Failed to update module');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloneModule = async (e) => {
    e.preventDefault();
    if (!cloneName.trim()) {
      toast.error('Please enter a name for the clone');
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/education-modules/${selectedModule._id}/clone`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: cloneName,
          reward: { type: 'credit', creditAmount: cloneReward }
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Clone "${data.title}" created successfully!`);
        setShowCloneModal(false);
        setCloneName('');
        setSelectedModule(null);
        if (onRefresh) onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create clone');
      }
    } catch (error) {
      console.error('Error creating clone:', error);
      toast.error('Failed to create clone');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCloneModal = (module) => {
    setSelectedModule(module);
    setCloneName(`${module.title} (Clone)`);
    setCloneReward(module.reward?.creditAmount || 5);
    setShowCloneModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRewardChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      reward: { ...prev.reward, [name]: value }
    }));
  };

  const handleScreenChange = (index, field, value) => {
    const updatedScreens = [...formData.screens];
    updatedScreens[index] = { ...updatedScreens[index], [field]: value };
    setFormData(prev => ({ ...prev, screens: updatedScreens }));
  };

  const handleScreenContentChange = (index, content) => {
    const updatedScreens = [...formData.screens];
    updatedScreens[index] = { ...updatedScreens[index], content };
    setFormData(prev => ({ ...prev, screens: updatedScreens }));
  };

  const addScreen = () => {
    setFormData(prev => ({
      ...prev,
      screens: [...prev.screens, { title: '', content: '', type: 'text' }]
    }));
  };

  const insertScreenAbove = (index) => {
    const newScreen = { title: '', content: '', type: 'text' };
    const updatedScreens = [...formData.screens];
    updatedScreens.splice(index, 0, newScreen);
    setFormData(prev => ({ ...prev, screens: updatedScreens }));
  };

  const insertScreenBelow = (index) => {
    const newScreen = { title: '', content: '', type: 'text' };
    const updatedScreens = [...formData.screens];
    updatedScreens.splice(index + 1, 0, newScreen);
    setFormData(prev => ({ ...prev, screens: updatedScreens }));
  };

  const removeScreen = (index) => {
    if (formData.screens.length <= 1) {
      toast.error('Module must have at least one screen');
      return;
    }
    setFormData(prev => ({
      ...prev,
      screens: prev.screens.filter((_, i) => i !== index)
    }));
  };

  const screenAccordionItems = formData.screens.map((screen, index) => ({
    title: screen.title || `Screen ${index + 1}`,
    icon: screen.type === 'quiz' ? '📝' : '📄',
    badge: index + 1,
    content: (
      <div className="space-y-3">
        <input
          type="text"
          value={screen.title}
          onChange={(e) => handleScreenChange(index, 'title', e.target.value)}
          className="w-full border rounded p-2 text-sm"
          placeholder="Screen title"
        />
        <select
          value={screen.type}
          onChange={(e) => handleScreenChange(index, 'type', e.target.value)}
          className="w-full border rounded p-2 text-sm"
        >
          <option value="text">Text</option>
          <option value="quiz">Quiz</option>
        </select>
{screen.type === 'quiz' ? (
  <div className="space-y-3">
    <input
      type="text"
      value={screen.quizQuestion || ''}
      onChange={(e) => handleScreenChange(index, 'quizQuestion', e.target.value)}
      className="w-full border rounded p-2 text-sm"
      placeholder="Quiz question"
    />
    <div className="space-y-2">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-500 w-8">{i + 1}.</span>
          <input
            type="text"
            value={screen.quizOptions?.[i] || ''}
            onChange={(e) => {
              const options = [...(screen.quizOptions || ['', '', '', ''])];
              options[i] = e.target.value;
              handleScreenChange(index, 'quizOptions', options);
            }}
            className="flex-1 border rounded p-2 text-sm"
            placeholder={`Option ${i + 1}`}
          />
        </div>
      ))}
    </div>
    <input
      type="number"
      value={screen.quizCorrectAnswer !== undefined ? screen.quizCorrectAnswer : ''}
      onChange={(e) => handleScreenChange(index, 'quizCorrectAnswer', parseInt(e.target.value))}
      className="w-full border rounded p-2 text-sm"
      placeholder="Correct answer (1-4)"
      min="1"
      max="4"
    />
    <textarea
      value={screen.quizExplanation || ''}
      onChange={(e) => handleScreenChange(index, 'quizExplanation', e.target.value)}
      className="w-full border rounded p-2 text-sm"
      rows="2"
      placeholder="Explanation for correct answer"
    />
  </div>
) : (
  <RichTextEditor
    content={screen.content}
    onChange={(content) => handleScreenContentChange(index, content)}
    placeholder="Write your content here..."
  />
)}
        <div className="flex gap-2 pt-2 border-t">
          <button
            type="button"
            onClick={() => insertScreenAbove(index)}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            ↑ Insert Above
          </button>
          <button
            type="button"
            onClick={() => insertScreenBelow(index)}
            className="text-green-600 hover:text-green-800 text-sm"
          >
            ↓ Insert Below
          </button>
          <button
            type="button"
            onClick={() => removeScreen(index)}
            className="text-red-500 hover:text-red-700 text-sm ml-auto"
          >
            Remove
          </button>
        </div>
      </div>
    )
  }));

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading modules...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">📚 Education Modules</h2>
          <p className="text-sm text-gray-500">
            Create educational content to help customers learn about your {role}
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          + Create Module
        </button>
      </div>

      <div className="flex gap-4 border-b mb-4">
        <button
          onClick={() => setActiveTab('modules')}
          className={`pb-2 px-1 ${activeTab === 'modules' ? 'border-b-2 border-green-600 text-green-600 font-semibold' : 'text-gray-500'}`}
        >
          📚 Modules
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`pb-2 px-1 ${activeTab === 'analytics' ? 'border-b-2 border-green-600 text-green-600 font-semibold' : 'text-gray-500'}`}
        >
          📊 Analytics
        </button>
      </div>

      {activeTab === 'modules' && (
        <>
          {modules.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-4">📝</div>
              <p>You haven't created any education modules yet.</p>
              <p className="text-sm mt-1">Create modules to educate customers about your products.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {modules.map(module => (
                <div key={module._id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{module.title}</h4>
                        {module.clonedFrom && (
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                            Clone
                          </span>
                        )}
                        {module.nextModuleSlug && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                            ➡️ Next: {module.nextModuleSlug}
                          </span>
                        )}
                        {!module.nextModuleSlug && (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                            End of Course
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{module.description}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                          {module.screens?.length || 0} screens
                        </span>
                        {module.reward?.creditAmount > 0 && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                            💰 {module.reward.creditAmount} credits
                          </span>
                        )}
                        <span className="text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                          {module.completions || 0} completions
                        </span>
                        {module.slug && (
                          <span className="text-xs bg-gray-50 text-gray-500 px-2 py-0.5 rounded font-mono">
                            {module.slug}
                          </span>
                        )}
                        {module.showOnStorefront && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                            Storefront
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4 flex-shrink-0">
                      <button
                        onClick={() => openCloneModal(module)}
                        className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                        title="Clone this module"
                      >
                        📋 Clone
                      </button>
                      <Link
                        to={`/learn/${module.slug}`}
                        target="_blank"
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                      >
                        Preview
                      </Link>
                      <button
                        onClick={() => openEditModal(module)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'analytics' && (
        <ModuleAnalytics modules={modules} role={role} />
      )}

      {/* Create Module Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowCreateModal(false)}
        >
          <div 
            className={`bg-white rounded-lg p-6 max-w-3xl w-full ${isMinimized ? 'max-h-12 overflow-hidden' : 'max-h-[90vh] overflow-y-auto'}`}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Create Education Module</h2>
              <div className="flex gap-2">
                <button 
                  onClick={() => setIsMinimized(!isMinimized)} 
                  className="text-gray-500 hover:text-gray-700"
                  title={isMinimized ? 'Expand' : 'Minimize'}
                >
                  {isMinimized ? '⤵' : '⤴'}
                </button>
                <button 
                  onClick={() => setShowCreateModal(false)} 
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>
            {!isMinimized && (
              <form 
                onSubmit={handleCreateModule} 
                className="space-y-4"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                    e.preventDefault();
                  }
                }}
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    className="w-full border rounded-lg p-2"
                    placeholder="Module title..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    className="w-full border rounded-lg p-2"
                    rows="3"
                    placeholder="Brief description..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <input
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleFormChange}
                      className="w-full border rounded-lg p-2"
                      placeholder="e.g., Farming, Recipes, Nutrition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                    <input
                      type="text"
                      name="tags"
                      value={formData.tags}
                      onChange={handleFormChange}
                      className="w-full border rounded-lg p-2"
                      placeholder="tomatoes, organic, seasonal"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Credit Reward</label>
                    <input
                      type="number"
                      name="creditAmount"
                      value={formData.reward.creditAmount}
                      onChange={handleRewardChange}
                      className="w-full border rounded-lg p-2"
                      min="0"
                      step="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Credits users earn upon completion (0 for none)</p>
                  </div>
                  <div className="flex items-center pt-6">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.showOnStorefront}
                        onChange={(e) => setFormData(prev => ({ ...prev, showOnStorefront: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">Show on Storefront</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Next Module (course order)</label>
                  <input
                    type="text"
                    name="nextModuleSlug"
                    value={formData.nextModuleSlug}
                    onChange={handleFormChange}
                    className="w-full border rounded-lg p-2"
                    placeholder="e.g., module-2-slug (leave blank if last in course)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Slug of the next module in this course series</p>
                  {formData.nextModuleSlug && (
                    <p className="text-xs text-blue-500 mt-1">
                      Preview: /learn/{formData.nextModuleSlug}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Screens</label>
                  <div className="max-h-96 overflow-y-auto pr-1">
                    <div onClick={(e) => e.stopPropagation()}>
                      <Accordion 
                        items={screenAccordionItems}
                        defaultOpen={0}
                        dark={false}
                        className="mt-2"
                        headerClassName="text-sm font-medium py-2 px-3"
                        contentClassName="p-3"
                        iconOpen="▼"
                        iconClosed="▶"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addScreen}
                    className="mt-2 text-sm text-green-600 hover:text-green-700"
                  >
                    + Add Screen
                  </button>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Creating...' : 'Create Module'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Edit Module Modal */}
      {showEditModal && editingModule && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowEditModal(false)}
        >
          <div 
            className={`bg-white rounded-lg p-6 max-w-3xl w-full ${isMinimized ? 'max-h-12 overflow-hidden' : 'max-h-[90vh] overflow-y-auto'}`}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Edit Module</h2>
              <div className="flex gap-2">
                <button 
                  onClick={() => setIsMinimized(!isMinimized)} 
                  className="text-gray-500 hover:text-gray-700"
                  title={isMinimized ? 'Expand' : 'Minimize'}
                >
                  {isMinimized ? '⤵' : '⤴'}
                </button>
                <button 
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }} 
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>
            {!isMinimized && (
              <form 
                onSubmit={handleUpdateModule} 
                className="space-y-4"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                    e.preventDefault();
                  }
                }}
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    className="w-full border rounded-lg p-2"
                    placeholder="Module title..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    className="w-full border rounded-lg p-2"
                    rows="3"
                    placeholder="Brief description..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <input
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleFormChange}
                      className="w-full border rounded-lg p-2"
                      placeholder="e.g., Farming, Recipes, Nutrition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                    <input
                      type="text"
                      name="tags"
                      value={formData.tags}
                      onChange={handleFormChange}
                      className="w-full border rounded-lg p-2"
                      placeholder="tomatoes, organic, seasonal"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Credit Reward</label>
                    <input
                      type="number"
                      name="creditAmount"
                      value={formData.reward.creditAmount}
                      onChange={handleRewardChange}
                      className="w-full border rounded-lg p-2"
                      min="0"
                      step="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Credits users earn upon completion (0 for none)</p>
                  </div>
                  <div className="flex items-center pt-6">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.showOnStorefront}
                        onChange={(e) => setFormData(prev => ({ ...prev, showOnStorefront: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">Show on Storefront</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Next Module (course order)</label>
                  <input
                    type="text"
                    name="nextModuleSlug"
                    value={formData.nextModuleSlug}
                    onChange={handleFormChange}
                    className="w-full border rounded-lg p-2"
                    placeholder="e.g., module-2-slug (leave blank if last in course)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Slug of the next module in this course series</p>
                  {formData.nextModuleSlug && formData.nextModuleSlug === editingModule?.slug && (
                    <p className="text-xs text-red-500 mt-1">⚠️ Cannot link to itself!</p>
                  )}
                  {formData.nextModuleSlug && formData.nextModuleSlug !== editingModule?.slug && (
                    <p className="text-xs text-blue-500 mt-1">
                      Preview: /learn/{formData.nextModuleSlug}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Screens</label>
                  <div className="max-h-96 overflow-y-auto pr-1">
                    <div onClick={(e) => e.stopPropagation()}>
                      <Accordion 
                        items={screenAccordionItems}
                        defaultOpen={0}
                        dark={false}
                        className="mt-2"
                        headerClassName="text-sm font-medium py-2 px-3"
                        contentClassName="p-3"
                        iconOpen="▼"
                        iconClosed="▶"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addScreen}
                    className="mt-2 text-sm text-green-600 hover:text-green-700"
                  >
                    + Add Screen
                  </button>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Clone Modal */}
      {showCloneModal && selectedModule && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowCloneModal(false);
            setSelectedModule(null);
            setCloneName('');
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">📋 Clone Module</h2>
              <button
                onClick={() => {
                  setShowCloneModal(false);
                  setSelectedModule(null);
                  setCloneName('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Create a full copy of <strong>"{selectedModule.title}"</strong> with a new title and tracking.
            </p>
            
            <form onSubmit={handleCloneModule} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clone Title *
                </label>
                <input
                  type="text"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  className="w-full border rounded-lg p-2"
                  placeholder="e.g., Overview (Farmers Edition)"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This will be the title of the cloned module. A unique slug will be generated automatically.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credit Reward
                </label>
                <input
                  type="number"
                  value={cloneReward}
                  onChange={(e) => setCloneReward(parseInt(e.target.value) || 0)}
                  className="w-full border rounded-lg p-2"
                  min="0"
                  step="1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Credits users earn for completing this clone. Change this to test different incentives.
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                💡 The clone will include all screens, images, and content from the original.
                You can edit the clone separately after creation.
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCloneModal(false);
                    setSelectedModule(null);
                    setCloneName('');
                  }}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !cloneName.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Creating...' : '📋 Create Clone'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
